import React from "react";
import type { WarmupPhase } from "../hooks/useWarmup";
import { WARMUP_MESSAGES } from "./WarmupMessages";

// Keyframes only — inline styles handle everything else
const KEYFRAMES = `
  @keyframes vw-pulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.72; }
  }
  @keyframes vw-shimmer {
    0%   { transform: translateX(-100%); }
    100% { transform: translateX(350%); }
  }
  @keyframes vw-d1 { 0%,20%{ opacity:0 } 30%,80%{ opacity:1 } 100%{ opacity:0 } }
  @keyframes vw-d2 { 0%,40%{ opacity:0 } 50%,90%{ opacity:1 } 100%{ opacity:0 } }
  @keyframes vw-d3 { 0%,60%{ opacity:0 } 70%,100%{ opacity:1 } }
  .vw-logo { height: 40px; max-width: 70vw; }
  .vw-logo svg { height: 100%; width: auto; max-width: 100%; }
  @media (max-width: 640px) {
    .vw-logo { height: 32px; }
    .vw-msg { font-size: 13.5px !important; }
  }
`;

function AnimatedDots() {
  const s = (anim: string): React.CSSProperties => ({
    animation: anim,
    opacity: 0,
    color: "#6b7280",
  });
  return (
    <>
      <span style={s("vw-d1 1.6s infinite")}>.</span>
      <span style={s("vw-d2 1.6s infinite")}>.</span>
      <span style={{ animation: "vw-d3 1.6s infinite", color: "#6b7280" }}>.</span>
    </>
  );
}

// Fisher-Yates shuffle, returns new array
function shuffled(arr: number[]): number[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function makeDeck(excluding = -1): number[] {
  const all = Array.from({ length: WARMUP_MESSAGES.length }, (_, i) => i).filter(i => i !== excluding);
  return shuffled(all);
}

const DOT_PALETTE = ["#2FBF71", "#4C75AB", "#1670E7"];
function pickDotColor(): string {
  try {
    const raw = window.localStorage.getItem("vw-color-idx");
    const idx = raw ? parseInt(raw, 10) : 0;
    const next = (Number.isFinite(idx) ? idx : 0) % DOT_PALETTE.length;
    window.localStorage.setItem("vw-color-idx", String(next + 1));
    return DOT_PALETTE[next];
  } catch {
    return DOT_PALETTE[0];
  }
}

export default function Warmup({ phase }: { phase: WarmupPhase }) {
  const dotColor = React.useMemo(() => pickDotColor(), []);
  const deckRef = React.useRef<number[]>([]);
  const [msgIdx, setMsgIdx] = React.useState(() => {
    const deck = makeDeck();
    deckRef.current = deck.slice(1);
    return deck[0];
  });
  const [msgVisible, setMsgVisible] = React.useState(true);

  React.useEffect(() => {
    if (phase !== "loading") return;
    const timer = setInterval(() => {
      setMsgVisible(false);
      setTimeout(() => {
        setMsgIdx((prev) => {
          if (deckRef.current.length === 0) {
            // Cycle complete — reshuffle avoiding last shown message
            const newDeck = makeDeck(prev);
            deckRef.current = newDeck.slice(1);
            return newDeck[0];
          }
          return deckRef.current.shift()!;
        });
        setMsgVisible(true);
      }, 500);
    }, 20000);
    return () => clearInterval(timer);
  }, [phase]);

  const msg = WARMUP_MESSAGES[msgIdx];

  // ── Root: full-screen overlay, pixel-perfect centered block ─────────────
  // The inner block has a fixed pixel height (172px = 62 logo + 24 gap + 60 msg + 24 gap + 2 bar).
  // Centering the block as a unit means NO child change can shift the bar position.
  const rootStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "#f9fafb",
    zIndex: 99999,
    fontFamily: "'Rubik', sans-serif",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: phase === "fading" ? 0 : 1,
    transition: "opacity 600ms ease-out",
  };

  // Fixed-height block — the entire content is locked to exactly 172px tall.
  // justifyContent: center on the root distributes space outside this block only,
  // so any text reflow inside never changes where the bar sits on screen.
  const blockStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 24,
    flexShrink: 0,
  };

  const logoWrapStyle: React.CSSProperties = {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    animation: "vw-pulse 2.8s ease-in-out infinite",
  };

  const msgWrapStyle: React.CSSProperties = {
    width: 460,
    maxWidth: "88vw",
    minHeight: 60,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 12px",
  };

  const msgStyle: React.CSSProperties = {
    color: "#111827",
    fontSize: 15.5,
    fontWeight: 400,
    letterSpacing: "0.01em",
    textAlign: "center",
    lineHeight: 1.6,
    opacity: msgVisible ? 1 : 0,
    transition: "opacity 0.4s ease-in-out",
  };

  const barTrackStyle: React.CSSProperties = {
    width: 140,
    height: 2,
    background: "#e5e7eb",
    borderRadius: 2,
    overflow: "hidden",
    flexShrink: 0,
  };

  const barFillStyle: React.CSSProperties = {
    width: "45%",
    height: "100%",
    background: "linear-gradient(90deg, #4C75AB 0%, #2FBF71 100%)",
    borderRadius: 2,
    animation: "vw-shimmer 1.6s ease-in-out infinite",
  };

  return (
    <>
      <style>{KEYFRAMES}</style>
      <div style={rootStyle}>
        {/* Single fixed-height block — bar can never shift regardless of text */}
        <div style={blockStyle}>
          <div className="vw-logo" style={logoWrapStyle}>
            <svg viewBox="0 0 165 34" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
              <g fill="#0b0b0b">
                <path d="M16.5022 33.5695C-3.49176 19.6415 5.20024 10.5985 18.7612 10.0495C24.8982 9.8055 25.0852 -0.350501 18.5192 0.480499C12.0452 1.3095 -1.93776 6.7725 0.636237 16.7095C2.59324 24.2635 11.8612 31.9195 16.5022 33.5695Z" />
                <path d="M22.2475 0.431499C42.2415 14.3585 33.5475 23.4025 19.9885 23.9505C13.8515 24.1955 13.6645 34.3555 20.2295 33.5195C26.7055 32.6915 40.6885 27.2315 38.1145 17.2915C36.1575 9.7375 26.8895 2.0815 22.2475 0.431499Z" />
                <path d="M51.1231 26.6605H48.8231L42.3721 9.6805H45.6591L50.1161 22.0805L54.7051 9.6805H57.9301L51.1231 26.6605ZM72.9781 16.5555C72.6828 15.2889 71.9905 14.1498 71.0021 13.3045C70.0555 12.5307 68.8633 12.121 67.6411 12.1495C66.3779 12.1254 65.146 12.5431 64.1581 13.3305C63.1523 14.1589 62.4409 15.2901 62.1301 16.5555H72.9781ZM62.0351 19.3005C62.2505 20.6411 62.9314 21.8629 63.9581 22.7515C64.8398 23.4882 65.9249 23.9393 67.0691 24.0447C68.2134 24.15 69.3626 23.9048 70.3641 23.3415C71.2007 22.8534 71.8875 22.1455 72.3501 21.2945H75.7901C75.1316 23.0176 73.9947 24.5167 72.5131 25.6155C71.0799 26.622 69.3641 27.1473 67.6131 27.1155C66.4313 27.13 65.2589 26.9041 64.1671 26.4515C63.1 25.9865 62.1391 25.3087 61.3431 24.4595C60.5451 23.6162 59.914 22.6295 59.4831 21.5515C59.0426 20.4766 58.8157 19.3262 58.8151 18.1645C58.8064 17.019 59.0137 15.8822 59.4261 14.8135C59.8416 13.7618 60.4523 12.7982 61.2261 11.9735C62.0144 11.0766 62.9883 10.3617 64.0802 9.87844C65.1722 9.39514 66.3561 9.15495 67.5501 9.1745C69.0032 9.16537 70.435 9.52418 71.7121 10.2175C72.9911 10.9132 74.0801 11.9116 74.8841 13.1255C75.3821 13.909 75.7534 14.7662 75.9841 15.6655C76.233 16.6986 76.3516 17.7589 76.3371 18.8215V19.3015L62.0351 19.3005ZM78.9851 4.1845H82.0091V26.6615H78.9851V4.1845ZM87.9571 18.0625C87.9366 18.8603 88.0773 19.6541 88.3705 20.3964C88.6638 21.1386 89.1038 21.8141 89.6641 22.3825C90.1997 22.9476 90.8475 23.3947 91.5659 23.695C92.2843 23.9953 93.0576 24.1423 93.8361 24.1265C94.583 24.1241 95.3214 23.9676 96.0051 23.6666C96.6887 23.3657 97.3029 22.9268 97.8091 22.3775C98.3562 21.8446 98.7903 21.2069 99.0853 20.5024C99.3803 19.798 99.5302 19.0412 99.5261 18.2775C99.5389 17.4868 99.3954 16.7013 99.1036 15.9663C98.8119 15.2313 98.3777 14.5611 97.8261 13.9945C97.3136 13.4297 96.6879 12.9793 95.9898 12.6724C95.2916 12.3655 94.5367 12.2092 93.7741 12.2135C93.0073 12.2126 92.2479 12.3634 91.5397 12.6573C90.8315 12.9513 90.1885 13.3825 89.6478 13.9261C89.107 14.4698 88.6792 15.1151 88.389 15.8248C88.0989 16.5346 87.952 17.2957 87.9571 18.0625ZM99.3751 26.6615V24.5745C98.6357 25.3572 97.7473 25.9841 96.7621 26.4185C95.7889 26.8377 94.7396 27.0512 93.6801 27.0455C92.5014 27.0782 91.3285 26.8682 90.2344 26.4285C89.1403 25.9888 88.1483 25.3288 87.3201 24.4895C86.4865 23.65 85.8325 22.6497 85.3977 21.5495C84.9629 20.4492 84.7565 19.272 84.7911 18.0895C84.7844 16.9264 84.9989 15.7726 85.4231 14.6895C85.8319 13.6375 86.4462 12.6775 87.2301 11.8655C88.0161 11.0183 88.9696 10.3437 90.0301 9.8845C91.1311 9.44046 92.31 9.22181 93.4971 9.2415C94.5973 9.229 95.6887 9.43851 96.7061 9.8575C97.7156 10.2909 98.6249 10.9278 99.3771 11.7285V9.6805H102.465V26.6605L99.3751 26.6615ZM109.102 26.6615H106.046V9.6805H108.918V11.6405C109.357 10.9544 109.953 10.3821 110.656 9.9705C111.355 9.59445 112.138 9.40049 112.932 9.4065V12.5945C112.386 12.5883 111.844 12.6924 111.339 12.9005C110.834 13.1085 110.376 13.4163 109.992 13.8055C109.314 14.8029 108.999 16.0036 109.102 17.2055V26.6615ZM117.326 18.1615C117.303 18.9406 117.433 19.7167 117.709 20.4454C117.986 21.1741 118.404 21.8412 118.938 22.4085C119.449 22.9606 120.072 23.3976 120.765 23.6907C121.458 23.9838 122.206 24.1262 122.958 24.1085C123.701 24.1234 124.439 23.9787 125.122 23.6842C125.804 23.3897 126.416 22.9523 126.915 22.4015C127.988 21.2442 128.563 19.7111 128.515 18.1335C128.568 16.5569 127.992 15.0239 126.915 13.8715C126.396 13.3375 125.775 12.9134 125.089 12.6247C124.403 12.3359 123.666 12.1883 122.921 12.1907C122.177 12.1932 121.44 12.3455 120.756 12.6388C120.072 12.932 119.453 13.3601 118.938 13.8975C117.857 15.0508 117.277 16.5856 117.326 18.1655M114.16 18.1965C114.151 17.0442 114.358 15.9004 114.771 14.8245C115.179 13.7753 115.791 12.8172 116.571 12.0055C117.384 11.1175 118.366 10.3993 119.458 9.8925C120.502 9.40303 121.64 9.14936 122.793 9.1495C123.967 9.13306 125.131 9.35743 126.215 9.80875C127.299 10.2601 128.278 10.9288 129.093 11.7735C129.911 12.6052 130.564 13.5849 131.016 14.6605C131.459 15.7108 131.687 16.8395 131.685 17.9795C131.684 19.1392 131.472 20.289 131.058 21.3725C130.227 23.5989 128.554 25.4097 126.4 26.4145C125.321 26.9037 124.149 27.1534 122.965 27.1465C121.785 27.1598 120.614 26.9322 119.525 26.4775C118.456 26.0164 117.493 25.3404 116.696 24.4915C115.898 23.6485 115.267 22.6617 114.836 21.5835C114.395 20.5086 114.168 19.3582 114.167 18.1965" />
              </g>
              <circle cx="20.5" cy="15.5" r="3.2" fill={dotColor} />
              <circle cx="135.5" cy="24.5" r="2.6" fill={dotColor} />
            </svg>
          </div>

          <div style={msgWrapStyle}>
            <span className="vw-msg" style={msgStyle}>
              {msg.text}
              {msg.dots && <AnimatedDots />}
            </span>
          </div>

          <div style={barTrackStyle}>
            <div style={barFillStyle} />
          </div>
        </div>
      </div>
    </>
  );
}
